/* ==========================================================================
   Regional Compliance Landing Page Configs - SEO Pages for HotelStack.io
   Part 2: Thailand/SEA, Australia, Japan + merged exports
   ========================================================================== */

import {
  Shield, FileText, CreditCard, Users, Globe, Receipt,
  Landmark, BadgeCheck, ScrollText, Building2, Scale,
} from 'lucide-react';
import type { RegionalComplianceConfig } from './seo-compliance.data';
import { EU_UK, UNITED_STATES, UAE_MIDDLE_EAST } from './seo-compliance.data';

/* -- Thailand / Southeast Asia -------------------------------------------- */

const THAILAND_SEA: RegionalComplianceConfig = {
  region: 'Thailand / Southeast Asia',
  slug: 'thailand-sea',
  title: 'Thailand & SEA Hotel Compliance Software',
  subtitle:
    'TM30 immigration reporting, 7% VAT automation, Thai hotel tax compliance, and Agoda/Traveloka channel integration for Southeast Asian hospitality.',
  flag: 'TH',
  complianceFeatures: [
    {
      icon: FileText,
      title: 'TM30 Immigration Reporting',
      description:
        'Automated TM30 form generation for foreign guest reporting to Thai Immigration. Passport data capture, bulk submission support, and electronic filing integration.',
    },
    {
      icon: Receipt,
      title: '7% VAT Automation',
      description:
        'Thai Revenue Department compliant 7% VAT calculation on accommodation and services. Automated tax invoice generation with required TIN and branch codes.',
    },
    {
      icon: Landmark,
      title: 'Hotel Tax & Tourism Levies',
      description:
        'Automated calculation of specific business tax where applicable, provincial tourism fund contributions, and municipal hospitality levies across Thai provinces.',
    },
    {
      icon: Users,
      title: 'Foreign Guest Registry',
      description:
        'Passport scanning and nationality tracking for immigration authority compliance. Automated guest statistics for Tourism Authority of Thailand (TAT) reporting.',
    },
    {
      icon: Globe,
      title: 'Thai & Regional Languages',
      description:
        'Full Thai language interface with support for English, Chinese (Simplified/Traditional), Japanese, and Korean for major SEA tourism source markets.',
    },
    {
      icon: Shield,
      title: 'PDPA Data Protection',
      description:
        'Thailand Personal Data Protection Act (PDPA) compliance with consent management, data subject rights workflows, and data breach notification procedures.',
    },
  ],
  taxDetails: [
    { name: 'Thai VAT', rate: '7%', description: 'Value Added Tax on accommodation services and taxable ancillary charges.' },
    { name: 'Specific Business Tax', rate: '3.3%', description: 'Applied to certain financial transactions and interest income from deposits.' },
    { name: 'Withholding Tax', rate: '1% - 5%', description: 'Withholding tax on service payments to vendors and contractors.' },
    { name: 'Provincial Tourism Levy', rate: 'Varies', description: 'Provincial tourism fund contributions in designated tourism zones.' },
    { name: 'Signboard Tax', rate: 'THB 200-500/yr', description: 'Annual signboard tax for hotel signage per municipal regulations.' },
    { name: 'Land & Building Tax', rate: '0.01% - 0.1%', description: 'Annual property tax based on assessed land and building value.' },
  ],
  guestRegistration:
    'HotelStack.io automates TM30 foreign guest notification required by Thai Immigration Bureau. Passport data is captured at check-in via OCR scanning, and TM30 forms are generated for electronic submission. Thai national ID card scanning is supported for domestic guests. Guest nationality statistics are compiled for Tourism Authority of Thailand (TAT) survey submissions.',
  dataProtection:
    'Full compliance with Thailand Personal Data Protection Act (PDPA, B.E. 2562). Includes lawful basis documentation, consent collection and withdrawal workflows, data subject access requests, data portability, right to erasure, and data breach notification within 72 hours. Data Protection Officer (DPO) appointment support and Records of Processing Activities (ROPA) generation.',
  faqItems: [
    { question: 'How does HotelStack.io handle TM30 reporting for Thai hotels?', answer: 'Our system automatically captures foreign guest passport details at check-in and generates TM30 notification forms. Bulk submission is supported for high-volume properties, and electronic filing integration with the Immigration Bureau online system streamlines the process.' },
    { question: 'Does the system support Thai VAT invoicing requirements?', answer: 'Yes. All tax invoices include your TIN, branch code, consecutive numbering, and all fields required by the Thai Revenue Department. VAT reports for monthly filing are generated automatically.' },
    { question: 'Is the system compliant with Thailand PDPA?', answer: 'Absolutely. HotelStack.io includes consent management, data subject rights workflows, breach notification procedures, and ROPA generation fully aligned with PDPA requirements effective since June 2022.' },
    { question: 'Which Southeast Asian OTAs are supported?', answer: 'We integrate with Agoda, Traveloka, Tiket.com, RedDoorz, and all major regional platforms alongside Booking.com, Expedia, and global OTAs. Two-way sync ensures rates and availability are always current.' },
  ],
};

/* -- Australia ------------------------------------------------------------ */

const AUSTRALIA: RegionalComplianceConfig = {
  region: 'Australia',
  slug: 'australia',
  title: 'Australian Hotel Compliance Software',
  subtitle:
    'GST automation with BAS reporting, state tourism levy tracking, Fair Work award wage compliance, and Privacy Act data handling for Australian hospitality.',
  flag: 'AU',
  complianceFeatures: [
    {
      icon: Receipt,
      title: '10% GST Automation',
      description:
        'Automated Goods and Services Tax calculation on all taxable supplies with ABN-inclusive tax invoices and BAS-ready quarterly or monthly reporting.',
    },
    {
      icon: Landmark,
      title: 'State Tourism Levies',
      description:
        'Track and calculate state-specific tourism levies including NT Tourism Marketing Duty, QLD tourism fees, and council-level accommodation levies.',
    },
    {
      icon: Scale,
      title: 'Fair Work Compliance',
      description:
        'Staff scheduling with Hospitality Industry General Award rates, weekend/public holiday penalty rates, overtime calculations, and split shift compliance.',
    },
    {
      icon: Shield,
      title: 'Privacy Act 1988',
      description:
        'Australian Privacy Principles (APPs) compliance with guest data collection notices, access/correction rights, cross-border disclosure controls, and data breach notification.',
    },
    {
      icon: BadgeCheck,
      title: 'Star Ratings Australia',
      description:
        'Track quality assurance metrics aligned with Star Ratings Australia (AAA Tourism) classification standards. Generate self-assessment reports.',
    },
    {
      icon: Building2,
      title: 'Fire & Safety Reporting',
      description:
        'Compliance tracking for state-specific fire safety regulations, essential safety measures reporting, and annual fire safety statement generation.',
    },
  ],
  taxDetails: [
    { name: 'GST', rate: '10%', description: 'Goods and Services Tax on accommodation, food, beverages, and services.' },
    { name: 'WET (Wine)', rate: '29%', description: 'Wine Equalisation Tax applicable to wine sales in hotel restaurants and bars.' },
    { name: 'NT Tourism Duty', rate: '5%', description: 'Northern Territory Tourism Marketing Duty on accommodation revenue.' },
    { name: 'Council Levies', rate: 'Varies', description: 'Local council accommodation levies in designated tourism areas.' },
    { name: 'Payroll Tax', rate: '4.75% - 6.85%', description: 'State-based payroll tax on wages above threshold — varies by state.' },
    { name: 'Land Tax', rate: 'Varies', description: 'State land tax based on unimproved land value — thresholds vary by state.' },
  ],
  guestRegistration:
    'Australian hotel guest registration is managed at the property level. HotelStack.io captures guest identity and contact details for property records and marketing consent (under APPs). International guest passport data is recorded for immigration compliance when required. Digital registration cards with electronic signatures replace paper forms for streamlined operations.',
  dataProtection:
    'Full compliance with the Privacy Act 1988 and Australian Privacy Principles (APPs). Includes APP 1 open and transparent management of personal information, APP 5 notification of collection, APP 6 use and disclosure controls, APP 8 cross-border disclosure safeguards, APP 11 security of personal information, and APP 12 access to personal information. Notifiable Data Breaches (NDB) scheme workflows included.',
  faqItems: [
    { question: 'How does HotelStack.io handle GST for Australian hotels?', answer: 'Our platform automatically applies 10% GST to all taxable supplies, generates ATO-compliant tax invoices with ABN, and provides BAS-ready summary reports for quarterly or monthly lodgement. GST-free supplies like long-term accommodation (28+ days) are correctly handled.' },
    { question: 'Does the system support Fair Work award wage compliance?', answer: 'Yes. Our staff scheduling module includes configurable Hospitality Industry General Award rates, Saturday/Sunday penalty rates, public holiday rates, overtime calculations, and split shift allowances. Rosters can be validated against award conditions before publishing.' },
    { question: 'How are state-specific tourism levies tracked?', answer: 'HotelStack.io maintains a database of state and territory tourism levies. The NT Tourism Marketing Duty, council-level accommodation levies, and other jurisdiction-specific charges are automatically calculated and reported.' },
    { question: 'Is guest data handled in compliance with Australian Privacy Principles?', answer: 'Yes. All guest data collection includes APP-compliant privacy notices, purpose limitation, access/correction rights, and cross-border disclosure controls. Our NDB scheme workflow ensures timely notification of eligible data breaches to the OAIC and affected individuals.' },
  ],
};

/* -- Japan ---------------------------------------------------------------- */

const JAPAN: RegionalComplianceConfig = {
  region: 'Japan',
  slug: 'japan',
  title: 'Japan Hotel Compliance Software',
  subtitle:
    'Accommodation tax automation, Ryokan Act licensing support, JCT qualified invoicing, and foreign guest registry compliance for Japanese hospitality.',
  flag: 'JP',
  complianceFeatures: [
    {
      icon: Landmark,
      title: 'Accommodation Tax',
      description:
        'Automated calculation of Tokyo, Osaka, Kyoto, Fukuoka, and other municipal accommodation taxes with tier-based rates per guest per night.',
    },
    {
      icon: ScrollText,
      title: 'Ryokan Act Licensing',
      description:
        'Compliance tracking for Ryokan-Gyoho (Inn Business Act) licensing requirements, capacity limits, facility standards, and periodic renewal reminders.',
    },
    {
      icon: Receipt,
      title: 'JCT Qualified Invoicing',
      description:
        'Generate qualified invoices (tekikaku seikyusho) under the Japanese Consumption Tax Invoice System with registered issuer numbers and compliant formatting.',
    },
    {
      icon: Users,
      title: 'Foreign Guest Registry',
      description:
        'Automated foreign guest passport recording as required by Japanese Immigration Control Act. Data storage with retrieval capabilities for inspection by authorities.',
    },
    {
      icon: Shield,
      title: 'APPI Data Protection',
      description:
        'Act on Protection of Personal Information (APPI) compliance with purpose specification, consent management, third-party provision records, and cross-border transfer restrictions.',
    },
    {
      icon: Building2,
      title: 'Fire Services Act',
      description:
        'Compliance tracking for Fire Services Act requirements including fire safety manager appointments, evacuation plans, and periodic inspection record management.',
    },
  ],
  taxDetails: [
    { name: 'JCT (Consumption Tax)', rate: '10%', description: 'Japanese Consumption Tax on accommodation, food, and services. Qualified invoice required.' },
    { name: 'Tokyo Accommodation Tax', rate: 'JPY 100-200/night', description: 'JPY 100 for room charges 10,000-14,999; JPY 200 for 15,000+ per person per night.' },
    { name: 'Osaka Accommodation Tax', rate: 'JPY 100-300/night', description: 'Three-tier system: JPY 100 (7,000-14,999), JPY 200 (15,000-19,999), JPY 300 (20,000+).' },
    { name: 'Kyoto Accommodation Tax', rate: 'JPY 200-1,000/night', description: 'JPY 200 (under 20,000), JPY 500 (20,000-49,999), JPY 1,000 (50,000+) per person per night.' },
    { name: 'Bathing Tax', rate: 'JPY 150/person', description: 'Nyutouzei applied per bather at hot spring (onsen) facilities.' },
    { name: 'Reduced JCT Rate', rate: '8%', description: 'Reduced rate for takeaway food and non-alcoholic beverages.' },
  ],
  guestRegistration:
    'HotelStack.io complies with Article 6 of the Ryokan-Gyoho (Inn Business Act) requiring guest registry maintenance. For foreign guests, passport nationality, passport number, and port of entry are captured as mandated by the Immigration Control and Refugee Recognition Act. The system maintains an electronic guest registry (shukuhakusha meibo) with the legally required 3-year retention period.',
  dataProtection:
    'Full compliance with Japan Act on Protection of Personal Information (APPI) as amended in 2022. Includes purpose of use specification and notification, consent management for sensitive personal information (including passport data), third-party provision records (Article 29), cross-border transfer restrictions (Article 28), and data breach notification to the Personal Information Protection Commission (PPC) within statutory timeframes.',
  faqItems: [
    { question: 'How does HotelStack.io handle Japanese accommodation taxes?', answer: 'Our system automatically calculates municipal accommodation taxes for Tokyo, Osaka, Kyoto, Fukuoka, and other implementing municipalities. Tier-based rates are applied per guest per night based on room charges, and tax reports are generated for municipal filing.' },
    { question: 'Does the system support Ryokan Act compliance?', answer: 'Yes. HotelStack.io tracks Ryokan-Gyoho licensing requirements including facility capacity limits, structural standards, and license renewal dates. Automated reminders ensure timely renewals and compliance documentation is centrally managed.' },
    { question: 'How does qualified invoicing work under the JCT Invoice System?', answer: 'All tax invoices are generated as qualified invoices (tekikaku seikyusho) with your registered issuer number (T + 13 digits), tax-inclusive pricing, itemized JCT amounts, and all fields required by the National Tax Agency. Both standard 10% and reduced 8% rates are correctly applied.' },
    { question: 'Is foreign guest passport recording automated?', answer: 'Yes. Our check-in module captures foreign guest passport data via OCR scanning — nationality, passport number, and port of entry as required by the Immigration Control Act. Records are stored electronically with the 3-year retention period mandated by the Inn Business Act.' },
  ],
};

/* -- India ---------------------------------------------------------------- */

const INDIA: RegionalComplianceConfig = {
  region: 'India',
  slug: 'india',
  title: 'India Hotel Compliance Software',
  subtitle:
    'GST automation with GSTIN invoicing, C-Form & FRRO foreign guest reporting, FSSAI food safety compliance, and state-level luxury tax handling for Indian hospitality.',
  flag: 'IN',
  complianceFeatures: [
    {
      icon: Receipt,
      title: 'GST Compliance & GSTIN Invoicing',
      description:
        'Automated 12% / 18% / 28% GST slab calculation based on room tariff. GSTIN-compliant tax invoices, HSN codes for hospitality services, Input Tax Credit tracking, and GSTR-1/GSTR-3B filing-ready reports.',
    },
    {
      icon: FileText,
      title: 'C-Form & Form F Registration',
      description:
        'Automated C-Form generation for foreign national guest registration as mandated by the Foreigners Act 1946 and Registration of Foreigners Rules 1992. Digital submission to local police and FRRO.',
    },
    {
      icon: Users,
      title: 'FRRO Foreign Guest Reporting',
      description:
        'Foreigners Regional Registration Office (FRRO) reporting with passport data capture, visa verification, Form C online submission via eFRRO portal, and arrival/departure notifications within 24 hours.',
    },
    {
      icon: BadgeCheck,
      title: 'FSSAI Food Safety License',
      description:
        'FSSAI license compliance tracking for hotel restaurants and room service. Menu labeling requirements, hygiene audit checklists, and license renewal reminders for food business operators.',
    },
    {
      icon: Shield,
      title: 'IT Act & DPDP Compliance',
      description:
        'Information Technology Act 2000 and Digital Personal Data Protection Act 2023 (DPDP) compliance. Consent management, data localization for Indian guests, and breach notification to CERT-In.',
    },
    {
      icon: Landmark,
      title: 'State Tourism & Luxury Tax',
      description:
        'State-specific compliance for tourism department registration, luxury tax (where still applicable), municipal corporation levies, and property tax obligations across Indian states.',
    },
  ],
  taxDetails: [
    { name: 'GST (Rooms < ₹7,500)', rate: '12%', description: 'GST on room tariff below ₹7,500 per night (no ITC available below ₹1,000).' },
    { name: 'GST (Rooms ≥ ₹7,500)', rate: '18%', description: 'GST on room tariff of ₹7,500 and above per night.' },
    { name: 'GST (Restaurant)', rate: '5%', description: 'GST on restaurant services without ITC (non-AC, standalone restaurants).' },
    { name: 'GST (Restaurant in Hotel ≥ ₹7,500)', rate: '18%', description: 'GST on restaurant services in hotels with room tariff ≥ ₹7,500 (with ITC).' },
    { name: 'TCS on Booking', rate: '1%', description: 'Tax Collected at Source by e-commerce operators on hotel bookings above ₹5 lakh.' },
    { name: 'Municipal/Property Tax', rate: 'Varies', description: 'Local municipal corporation property tax based on annual rateable value.' },
  ],
  guestRegistration:
    'HotelStack.io automates Form C submission for foreign guest registration as required under the Foreigners Act 1946. Passport details, visa information, and arrival data are captured at check-in via OCR scanning. The system generates Form C for digital submission to local police stations and FRRO via the eFRRO portal within the mandatory 24-hour window. Indian guest registration with Aadhaar/government ID capture is also supported for state police compliance.',
  dataProtection:
    'Compliance with the Information Technology Act 2000, IT (Reasonable Security Practices) Rules 2011, and the Digital Personal Data Protection Act 2023 (DPDP). Includes lawful purpose documentation, consent collection with clear notice in English and Hindi, data principal rights (access, correction, erasure, grievance), data fiduciary obligations, cross-border transfer controls, and breach notification to CERT-In and the Data Protection Board of India within statutory timeframes.',
  faqItems: [
    { question: 'How does HotelStack.io handle GST for Indian hotels?', answer: 'Our system automatically applies the correct GST slab (12% or 18%) based on declared room tariff. GSTIN-compliant tax invoices with HSN codes are generated, and monthly GSTR-1/GSTR-3B filing reports are provided. ITC reconciliation tools help maximize input credits.' },
    { question: 'Does the system automate C-Form and FRRO reporting?', answer: 'Yes. Foreign guest passport and visa data is captured at check-in via OCR. Form C is auto-generated and can be submitted digitally to local police and FRRO via the eFRRO portal within the mandatory 24-hour timeframe. Bulk submission is supported for high-volume properties.' },
    { question: 'Is FSSAI compliance tracked for hotel restaurants?', answer: 'HotelStack.io tracks FSSAI license status, expiry dates, and renewal reminders for all food service outlets. Menu labeling compliance, hygiene audit scheduling, and food safety training records are centrally managed.' },
    { question: 'Which Indian OTAs and payment methods are supported?', answer: 'We integrate with MakeMyTrip, Goibibo, Yatra, Cleartrip, EaseMyTrip, and all major Indian OTAs alongside global channels. UPI, Paytm, PhonePe, net banking, and card payments are supported via PaymentHooks.com integration.' },
  ],
};

/* =========================================================================
   Merged Exports — All 7 Regions
   ========================================================================= */

export const COMPLIANCE_CONFIGS: Record<string, RegionalComplianceConfig> = {
  'eu-uk': EU_UK,
  'united-states': UNITED_STATES,
  'uae-middle-east': UAE_MIDDLE_EAST,
  'thailand-sea': THAILAND_SEA,
  'australia': AUSTRALIA,
  'japan': JAPAN,
  'india': INDIA,
};

export const COMPLIANCE_FAQ: Record<string, { question: string; answer: string }[]> = Object.fromEntries(
  Object.entries(COMPLIANCE_CONFIGS).map(([slug, cfg]) => [slug, cfg.faqItems]),
);
